import * as React from "react"
import { createPortal } from "react-dom"
import { progress } from "./motion"

/** "Awarded Best in Design" on the home, opened: a black panel slides in from
 *  the right (the main menu's easings and speed) with the award's tab on its
 *  edge; the award turns in from the side (its rendered video, played and eased
 *  to a stop about 25 degrees off facing you), its two lines under it. The tab,
 *  Escape or a click beside it closes it again. */

// the video (the about page's portrait render), played from memory: every frame
// a keyframe, so each one can be shown in turn
const FILES = ["/about/award-720.mp4", "/about/award-720.webm"]
const FPS = 24
/** where it stops: frame 21, a quarter turn from facing (the full face is ~31) */
const STOP = 21.5 / FPS

const T = {
    open: 927, // the main menu: opening 927ms cubic-bezier(.85,0,.15,1)
    close: 769, // closing 769ms cubic-bezier(.8,0,.2,1)
    turnAt: 450,
    turn: 2400,
    text: [1100, 1260], // the two lines, word by word
    wordStep: 35,
    wordDur: 320,
}
const LINES = ["Honoured to have been awarded best in design", "by the Dutch Interactive Awards"]
/** the panel (px, from the design: 425 x 613, 20 from the screen's right and bottom) */
export const panelWidth = () => Math.min(425, Math.round(innerWidth * 0.4))

let fetched: Promise<Blob> | null = null
function videoFile(): Promise<Blob> {
    if (fetched) return fetched
    const v = document.createElement("video")
    const files = v.canPlayType('video/mp4; codecs="avc1.640028"') === "probably" || !v.canPlayType('video/webm; codecs="vp9"') ? FILES : [...FILES].reverse()
    const get = (k: number): Promise<Blob> =>
        fetch(files[k])
            .then((r) => (r.ok ? r.blob() : Promise.reject()))
            .catch((e) => (k + 1 < files.length ? get(k + 1) : Promise.reject(e)))
    fetched = get(0).catch((e) => {
        fetched = null
        throw e
    })
    return fetched
}
/** start loading it (a pointer on the tab) */
export const preloadAward = () => void videoFile().catch(() => {})

const bezier = (x1: number, y1: number, x2: number, y2: number) => (x: number) => {
    let t = x
    for (let i = 0; i < 8; i++) {
        const cx = 3 * (1 - t) * (1 - t) * t * x1 + 3 * (1 - t) * t * t * x2 + t * t * t - x
        const d = 3 * (1 - t) * (1 - t) * x1 + 6 * (1 - t) * t * (x2 - x1) + 3 * t * t * (1 - x2)
        if (Math.abs(cx) < 1e-5 || !d) break
        t = Math.max(0, Math.min(1, t - cx / d))
    }
    return 3 * (1 - t) * (1 - t) * t * y1 + 3 * (1 - t) * t * t * y2 + t * t * t
}
// the turn coming to rest: long and soft
const settle = bezier(0.16, 1, 0.3, 1)

// The award's tab (the Framer drawing, ASSETS.award): flat along the screen's
// edge, its two ends slanting in (1 across for 3 deep), rounded (r5) where they
// meet its inner side; drawn at any depth (it stands out on hover), and with
// curves joining it to the award's panel when that's open (r: their size)
function tabPath(d: number, l = 220, r = 0) {
    const y = d - 3.419,
        x = y / 3
    const f = (n: number) => n.toFixed(3)
    // (each curve: along the edge, round into the slanting end)
    const sx = 0.316 * r,
        sy = 0.949 * r
    return (
        // (and 2px into the panel, under its edge: no hairline between the two)
        `M${f(-r)} -2H${f(l + r)}V0Q${l} 0 ${f(l - sx)} ${f(sy)}L${f(l - x)} ${f(y)}A5 5 0 0 1 ${f(l - x - 4.744)} ${d}` +
        `H${f(x + 4.744)}A5 5 0 0 1 ${f(x)} ${f(y)}L${f(sx)} ${f(sy)}Q0 0 ${f(-r)} 0z`
    )
}
/** (deeper, it's longer too: its slanted ends take the same out of its inner side) */
const tabLength = (d: number) => 220 + (2 * (d - 30)) / 3
const TAB = { depth: 30, hover: 50, ms: 380, curve: 15 }
const ease = (x1: number, y1: number, x2: number, y2: number) => (x: number) => {
    let t = x
    for (let i = 0; i < 8; i++) {
        const cx = 3 * (1 - t) * (1 - t) * t * x1 + 3 * (1 - t) * t * t * x2 + t * t * t - x
        const dx = 3 * (1 - t) * (1 - t) * x1 + 6 * (1 - t) * t * (x2 - x1) + 3 * t * t * (1 - x2)
        if (Math.abs(cx) < 1e-5 || !dx) break
        t = Math.max(0, Math.min(1, t - cx / dx))
    }
    return 3 * (1 - t) * (1 - t) * t * y1 + 3 * (1 - t) * t * t * y2 + t * t * t
}
const quart = (k: number) => 1 - Math.pow(1 - k, 4)
/** the main menu's opening and closing (the panel slides with them) */
const PANEL = { open: 927, close: 769, opening: ease(0.85, 0, 0.15, 1), closing: ease(0.8, 0, 0.2, 1) }
type Tween = { v: number; from: number; to: number; t0: number; ms: number; f: (k: number) => number }
/** The tab: on hover it stands 20px further out from the edge (its label and
 *  arrow staying in its middle); with the panel open, curves join it to it. */
function awardTab(el: HTMLElement, set: { out?: boolean; open?: boolean }) {
    const st = ((el as any).__tab ||= {
        d: { v: TAB.depth, from: TAB.depth, to: TAB.depth, t0: 0, ms: 1, f: quart } as Tween,
        r: { v: 0, from: 0, to: 0, t0: 0, ms: 1, f: quart } as Tween,
        raf: 0,
    })
    const now0 = performance.now()
    const go = (tw: Tween, to: number, ms: number, f: (k: number) => number) => Object.assign(tw, { from: tw.v, to, t0: now0, ms, f })
    if (set.out !== undefined) go(st.d, set.out ? TAB.hover : TAB.depth, TAB.ms, quart)
    if (set.open !== undefined) go(st.r, set.open ? TAB.curve : 0, set.open ? PANEL.open : PANEL.close, set.open ? PANEL.opening : PANEL.closing)
    cancelAnimationFrame(st.raf)
    const svg = el.querySelector("svg")!,
        path = svg.firstElementChild as SVGPathElement
    const inner = el.querySelectorAll<HTMLElement>(".h-award-arrow, .h-award-label")
    const step = (now: number) => {
        let moving = false
        for (const tw of [st.d, st.r] as Tween[]) {
            const k = Math.min(1, Math.max(0, (now - tw.t0) / tw.ms))
            tw.v = tw.from + (tw.to - tw.from) * tw.f(k)
            moving ||= k < 1
        }
        const d = st.d.v,
            l = tabLength(d)
        svg.setAttribute("viewBox", `0 0 ${l.toFixed(2)} ${d.toFixed(2)}`)
        svg.style.width = l.toFixed(2) + "px"
        svg.style.height = d.toFixed(2) + "px"
        svg.style.left = (30 - d / 2 - l / 2).toFixed(2) + "px"
        svg.style.top = (110 - d / 2).toFixed(2) + "px"
        path.setAttribute("d", tabPath(d, l, st.r.v))
        inner.forEach((n) => (n.style.translate = `${(-(d - TAB.depth) / 2).toFixed(2)}px 0`))
        if (moving) st.raf = requestAnimationFrame(step)
    }
    st.raf = requestAnimationFrame(step)
}

export default function AwardPanel({
    open,
    onToggle,
    tabRef,
    tabStyle,
    arrow,
}: {
    open: boolean
    onToggle: (open: boolean) => void
    tabRef: (el: HTMLElement | null) => void
    tabStyle: React.CSSProperties
    arrow: string
}) {
    const onClose = () => onToggle(false)
    const tab = React.useRef<HTMLButtonElement | null>(null)
    const panel = React.useRef<HTMLDivElement>(null)
    const video = React.useRef<HTMLVideoElement>(null)
    const lines = React.useRef<(HTMLParagraphElement | null)[]>([])
    const api = React.useRef<{ open: (o: boolean) => void } | null>(null)
    const closeRef = React.useRef(onClose)
    closeRef.current = onClose

    React.useLayoutEffect(() => {
        const el = panel.current!,
            v = video.current!
        let raf = 0,
            url = "",
            ready = false,
            loading = false,
            openAt = 0,
            turnFrom = 0
        const html = document.documentElement
        const size = () => html.style.setProperty("--aw-w", panelWidth() + "px")
        size()

        // one seek at a time: the next once the last is shown
        let pending: number | null = null
        const seek = (t: number) => {
            if (v.seeking) return void (pending = t)
            if (Math.abs(v.currentTime - t) > 0.001) v.currentTime = t
        }
        const onSeeked = () => {
            if (pending == null) return
            const t = pending
            pending = null
            seek(t)
        }
        v.addEventListener("seeked", onSeeked)
        const load = () => {
            if (loading) return
            loading = true
            videoFile()
                .then((b) => {
                    url = URL.createObjectURL(b)
                    v.src = url
                    v.addEventListener("loadeddata", () => (ready = true), { once: true })
                    v.load()
                    // (iOS shows a video's frames only once it has played: a moment, muted)
                    v.play?.()
                        .then(() => v.pause())
                        .catch(() => {})
                })
                .catch(() => (loading = false))
        }

        const frame = (now: number) => {
            raf = 0
            if (!openAt) return
            const t = now - openAt
            // the turn: from the side to its stop, easing to rest (once the video is in)
            if (ready && !turnFrom && t >= T.turnAt) turnFrom = now
            const turn = turnFrom ? settle(Math.min(1, (now - turnFrom) / T.turn)) : 0
            if (ready) seek(turn * STOP)
            const s = turnFrom ? now - turnFrom + T.turnAt : 0
            lines.current.forEach((p, j) => {
                for (const w of Array.from(p!.children) as HTMLElement[]) {
                    const i = Number(w.dataset.i)
                    const k = progress(s, T.text[j] + T.wordStep * i, T.wordDur, "out")
                    w.style.opacity = String(k)
                    w.style.transform = k < 1 ? `translateY(${(100 * (1 - k)).toFixed(1)}%)` : ""
                }
            })
            if (!turnFrom || now - turnFrom < T.turn + 400) raf = requestAnimationFrame(frame)
        }
        let closedAt: ReturnType<typeof setTimeout> | undefined
        api.current = {
            open(o) {
                clearTimeout(closedAt)
                el.toggleAttribute("data-open", o)
                if (o) {
                    el.removeAttribute("data-away")
                    load()
                    openAt = performance.now()
                    turnFrom = 0
                    // (from the side again, each time)
                    if (ready) seek(0)
                    lines.current.forEach((p) => (Array.from(p!.children) as HTMLElement[]).forEach((w) => (w.style.opacity = "0")))
                    cancelAnimationFrame(raf)
                    raf = requestAnimationFrame(frame)
                } else {
                    openAt = 0
                    // (its contents hidden once it's slid away)
                    closedAt = setTimeout(() => el.toggleAttribute("data-away", true), T.close + 50)
                }
            },
        }

        // closing it: Escape, or a click beside it (not on it, nor on its tab)
        const key = (e: KeyboardEvent) => e.key === "Escape" && el.hasAttribute("data-open") && closeRef.current()
        const down = (e: PointerEvent) => {
            if (!el.hasAttribute("data-open")) return
            const t = e.target as Element
            if (el.contains(t)) return
            closeRef.current()
        }
        // a page change: the tab (and the panel, if open) goes off the edge
        const leave = () => {
            el.toggleAttribute("data-leaving", true)
            if (el.hasAttribute("data-open")) closeRef.current()
        }
        addEventListener("nto:leave", leave)
        addEventListener("keydown", key)
        addEventListener("pointerdown", down)
        addEventListener("resize", size)
        return () => {
            cancelAnimationFrame(raf)
            clearTimeout(closedAt)
            removeEventListener("nto:leave", leave)
            removeEventListener("keydown", key)
            removeEventListener("pointerdown", down)
            removeEventListener("resize", size)
            v.removeEventListener("seeked", onSeeked)
            if (url) URL.revokeObjectURL(url)
        }
    }, [])
    React.useEffect(() => {
        api.current?.open(open)
        if (tab.current) awardTab(tab.current, { open })
    }, [open])

    return createPortal(
        <div ref={panel} className="h-aw">
            {/* the tab: on the panel's edge (sticking out from the screen's while it's away), so the two are always together */}
            <button
                ref={(el) => {
                    tabRef(el)
                    tab.current = el
                }}
                className="h-award"
                style={tabStyle}
                aria-haspopup="dialog"
                aria-expanded={open}
                onPointerEnter={(e) => (preloadAward(), awardTab(e.currentTarget, { out: true }))}
                onPointerLeave={(e) => awardTab(e.currentTarget, { out: false })}
                onFocus={preloadAward}
                onClick={() => onToggle(!open)}
            >
                <svg className="h-award-badge" viewBox="0 0 220 30" preserveAspectRatio="none" aria-hidden="true">
                    <path d={tabPath(30)} />
                </svg>
                <img src={arrow} alt="" width={10} height={15} className="h-award-arrow" />
                <span className="h-award-label">Awarded Best in Design</span>
            </button>
            <div className="h-aw-in" role="dialog" aria-label="Awarded Best in Design by the Dutch Interactive Awards" aria-hidden={!open}>
            <div className="h-aw-box">
                <video ref={video} className="h-aw-video" muted playsInline preload="auto" disablePictureInPicture aria-hidden="true" />
            </div>
            <div className="h-aw-text">
                {LINES.map((line, j) => (
                    <p key={j} ref={(el) => (lines.current[j] = el)} className="h-aw-line">
                        {line.split(" ").map((w, k) => (
                            <React.Fragment key={k}>
                                {k > 0 && " "}
                                <span data-i={k}>{w}</span>
                            </React.Fragment>
                        ))}
                    </p>
                ))}
            </div>
            </div>
        </div>,
        document.body
    )
}
